import { ErrorBoundary } from '@sentry/react';
import { useSearch } from '@tanstack/react-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useLocalStorage } from 'usehooks-ts';
import {
  ChartActionId,
  IChartingLibraryWidget,
  IExecutionLineAdapter,
  IOrderLineAdapter,
  IPositionLineAdapter,
  ResolutionString,
  SeriesType,
  widget,
} from '../../charting_library';
import { POSITION_STOP_ORDERS } from '../../constants/featureFlags';
import { ASSET_ROUTE_URL } from '../../constants/routes';
import { VOLUME_BASED_INDICATORS } from '../../constants/tradingview';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FillParams, useFills } from '../../hooks/useFills';
import { useFlattenedPages } from '../../hooks/useFlattenedPages';
import { useHandleExpiredSigner } from '../../hooks/useHandleExpiredSigner';
import { useModalState } from '../../hooks/useModalState';
import { useOrderCancelMutation } from '../../hooks/useOrderCancelMutation';
import { useOrderEditMutation } from '../../hooks/useOrderEditMutation';
import { Order, useOpenOrders, usePositionStopOrdersByProductId } from '../../hooks/useOrders';
import { usePositions } from '../../hooks/usePositions';
import { ProductType } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { cn } from '../../lib/classNameHelpers';
import { useArchiveDb } from '../../lib/config';
import { Gwei } from '../../lib/gwei';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { alignToValidSize, getDecimalPlaces } from '../../lib/math';
import { sortOrdersByStopType, validateOrderFormData } from '../../lib/orderHelpers';
import {
  createTradingViewConfig,
  renderFills,
  renderOrderAndPositionLines,
  type ChartPriceType,
} from '../../lib/tradingViewHelpers';
import { tryCatch } from '../../lib/tryCatch';
import { AnimatedLogo } from '../AnimatedLogo/AnimatedLogo';
import { LiqPriceLineUpdater } from './LiqPriceLineUpdater';
import { ChartToStateResolutionSync, StateToChartResolutionSync } from './ResolutionSyncComponents';
import { SymbolUpdater } from './SymbolUpdater';
import { useTradingViewPaused } from './useTradingViewPaused';
import { useTransitionState } from './useTransitionState';

type TradingViewChartProps = {
  product: ProductType;
  chartResolution: ResolutionString;
  chartType: SeriesType;
  priceType: ChartPriceType;
  // Abstraction around currentChart.executeActionById
  chartActionQueue?: ChartActionId[];
  className?: string;
  setChartResolution: (resolution: ResolutionString) => void;
  setChartType: (chartType: SeriesType) => void;
  setChartActionQueue?: React.Dispatch<React.SetStateAction<ChartActionId[]>>;
};

const TVChartContainerInner: React.FC<TradingViewChartProps> = ({
  product,
  chartResolution,
  chartType,
  priceType,
  className,
  setChartResolution,
  setChartType,
  chartActionQueue,
  setChartActionQueue,
}) => {
  const tradingViewPaused = useTradingViewPaused();
  const symbol = useArchiveDb ? `${product.ticker}-Perp` : `${product.ticker}`;
  const widgetRef = useRef<IChartingLibraryWidget | null>(null);
  const orderLinesMapRef = useRef(new Map<string, IOrderLineAdapter>());
  const stopLossLineRef = useRef<IOrderLineAdapter | undefined>(undefined);
  const takeProfitLineRef = useRef<IOrderLineAdapter | undefined>(undefined);
  const positionLineRef = useRef<IPositionLineAdapter | undefined>(undefined);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const [showOrderInformation] = useLocalStorage(localStorageKeys.showOrderInformation, true);
  const [showPositionsInformation] = useLocalStorage(
    localStorageKeys.showPositionsInformation,
    true
  );
  const { showAllMarkets } = useSearch({ from: ASSET_ROUTE_URL });
  const { signerExpired, withSignerCheck } = useHandleExpiredSigner();

  // Transition states for chart operations
  const resolutionTransition = useTransitionState();
  const symbolTransition = useTransitionState();
  // Even though we only care about order for the current product, making the filters match the other order queries saves us some requests
  const productIdFilter = showAllMarkets ? undefined : [product.id];
  const { data: orderData } = useOpenOrders({
    subaccountId: selectedSubaccount?.id,
    productIds: productIdFilter,
  });

  const orders = useMemo(
    () =>
      sortOrdersByStopType(
        orderData?.pages
          .flatMap((page) => page.orders)
          ?.filter((order) => order.productId === product.id) || []
      ),
    [orderData?.pages, product.id]
  );

  // Fills
  const useFillsParams = useMemo(
    () =>
      ({
        subaccountId: selectedSubaccount?.id || '',
        productIds: [product.id],
        orderBy: 'createdAt',
        order: 'desc',
      }) as const satisfies FillParams,
    [selectedSubaccount?.id, product.id]
  );
  const { data: fillData } = useFills(useFillsParams);
  const fills = useFlattenedPages('data', fillData?.pages);
  const fillsMap = useRef(new Map<string, IExecutionLineAdapter>());
  const [showFillsSetting] = useLocalStorage(localStorageKeys.showFills, true);

  const { data: allPositions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const position = useMemo(
    () => allPositions?.find((position) => position.productId === product.id),
    [allPositions, product.id]
  );
  const { value: stopOrderEnabled } = useFeatureFlag(POSITION_STOP_ORDERS);
  const { data: stopOrdersByProductId } = usePositionStopOrdersByProductId({
    subaccountId: stopOrderEnabled ? selectedSubaccount?.id : undefined,
    productId: product.id,
  });
  const stopOrders = stopOrdersByProductId?.[product.id];
  const stopLossOrder = stopOrders?.stopLoss;
  const takeProfitOrder = stopOrders?.takeProfit;

  const { mutate: cancelOrderMutation } = useOrderCancelMutation();

  const cancelOrder = useCallback(
    (orderIds: string[]) => withSignerCheck(cancelOrderMutation)(orderIds),
    [cancelOrderMutation, withSignerCheck]
  );
  const { mutate: editOrder, isPending: isEditingOrder } = useOrderEditMutation(product);
  const handleEditOrder = useCallback(
    (order: Order, newPriceRaw: number) => {
      withSignerCheck(() => {
        // calculate the new limit price based off of the tick size
        const newPrice = alignToValidSize(Gwei.toGwei(newPriceRaw), product.tickSize);

        const sharedOrder = {
          type: order.type,
          amount: order.availableQuantity,
          side: order.side,
          reduceOnly: order.reduceOnly ?? false,
          close: order.close ?? false,
          ...(order.type === 'LIMIT'
            ? { postOnly: order.postOnly ?? false, timeInForce: order.timeInForce }
            : {}),
        };
        const validatedOrder = validateOrderFormData(
          order.stopType !== undefined
            ? {
                ...sharedOrder,
                stopPrice: newPrice,
                stopType: order.stopType,
                otocoGroupId: order.otocoGroupId,
              }
            : {
                ...sharedOrder,
                price: newPrice,
              }
        );
        if (!validatedOrder) {
          return;
        }

        editOrder({
          existingOrderId: order.id,
          newOrderData: validatedOrder,
        });
      })();
    },
    [editOrder, product.tickSize, withSignerCheck]
  );
  const setPositionToCloseModal = useModalState((state) => state.setClosePositionModal);
  const closePosition = useCallback(
    (productId: string) =>
      withSignerCheck(() =>
        setPositionToCloseModal({ openOrClosed: 'open', productId, orderType: 'MARKET' })
      )(),
    [withSignerCheck, setPositionToCloseModal]
  );
  const tickSize = product.tickSize;
  const productDecimals = getDecimalPlaces(product.lotSize);
  const priceDecimals = getDecimalPlaces(tickSize);

  const widgetOptions = useMemo(
    () => createTradingViewConfig(priceType, useArchiveDb),
    [priceType]
  );

  const [isLoading, setIsLoading] = useState(true);

  const isChartReady = useCallback(() => {
    if (
      !widgetRef.current ||
      isLoading ||
      symbolTransition.transitionRef.current ||
      symbolTransition.isTransitioning
    ) {
      return false;
    }

    return true;
  }, [isLoading, symbolTransition.isTransitioning, symbolTransition.transitionRef]);

  const doesSymbolMatch = useCallback(() => {
    if (isLoading) {
      return false;
    }

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!chart) {
      return false;
    }

    const currentSymbol = useArchiveDb
      ? chart.symbol()
      : chart.symbol().replace('CRYPTO.', '').replace('/', '');

    if (currentSymbol.toLowerCase() === symbol.toLowerCase()) {
      return true;
    }

    return false;
  }, [isLoading, symbol]);

  // Initialization of chart
  // We only expect this to be called again if the priceType or paused changes.
  useEffect(() => {
    if (widgetRef.current) {
      widgetRef.current.remove();
      widgetRef.current = null;
    }

    if (tradingViewPaused) {
      return;
    }

    widgetRef.current = new widget({
      ...widgetOptions,
      symbol,
      interval: chartResolution,
    });

    widgetRef.current.onChartReady(async () => {
      setIsLoading(false);
      if (widgetRef.current) {
        // Apply tick size override for external data feeds when using Pyth feed
        const priceScale = Math.pow(10, priceDecimals);
        const minMove = Gwei.fromGwei(tickSize);
        const frac = false;
        const pythMinTick = `${priceScale},${minMove},${frac}`;
        const minTick = useArchiveDb ? 'default' : pythMinTick;

        tryCatch(() =>
          widgetRef.current?.applyOverrides({ 'mainSeriesProperties.minTick': minTick })
        );
        // Remove volume-based indicators when price type changes
        const chart = tryCatch(() => widgetRef.current?.activeChart());
        if (chart && (!useArchiveDb || priceType === 'oracle-price')) {
          tryCatch(() =>
            chart.getAllStudies().forEach((study) => {
              if (VOLUME_BASED_INDICATORS.find((indicator) => indicator === study.name)) {
                chart.removeEntity(study.id);
              }
            })
          );
        }

        tryCatch(() =>
          widgetRef.current?.subscribe('onAutoSaveNeeded', () => {
            // Use saveChartToServer which trigger the save_load_adapter
            tryCatch(() =>
              widgetRef.current?.saveChartToServer(
                () => {},
                () => {},
                { defaultChartName: 'Ethereal Chart' }
              )
            );
          })
        );
      }
    });

    // Remove chart on unmount
    return () => {
      if (widgetRef.current) {
        widgetRef.current.remove();
        widgetRef.current = null;
      }
    };
    // When symbol changes, we call `setSymbol` instead of recreating the widget
    // Same thing for resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetOptions, tradingViewPaused]);

  // Set isLoading when priceType changes. onChartReady will be called when
  // the data is loaded.
  useEffect(() => {
    orderLinesMapRef.current.clear();
    fillsMap.current.clear();
    stopLossLineRef.current = undefined;
    takeProfitLineRef.current = undefined;
    positionLineRef.current = undefined;
    setIsLoading(true);
  }, [priceType, tradingViewPaused]);

  // Handle order and position lines
  useEffect(() => {
    if (!isChartReady()) {
      return;
    }

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) {
      return;
    }

    // Don't render lines if we're editing an order (otherwise the order line will flicker)
    if (isEditingOrder) {
      return;
    }

    tryCatch(() =>
      renderOrderAndPositionLines(
        chart,
        orders || [],
        orderLinesMapRef.current,
        stopLossOrder,
        stopLossLineRef,
        takeProfitOrder,
        takeProfitLineRef,
        position,
        positionLineRef,
        productDecimals,
        priceDecimals,
        cancelOrder,
        handleEditOrder,
        closePosition,
        showOrderInformation,
        showPositionsInformation,
        signerExpired
      )
    );
  }, [
    cancelOrder,
    handleEditOrder,
    isChartReady,
    doesSymbolMatch,
    isEditingOrder,
    orders,
    position,
    closePosition,
    priceDecimals,
    productDecimals,
    showOrderInformation,
    showPositionsInformation,
    stopLossOrder,
    takeProfitOrder,
    signerExpired,
  ]);

  // handle Fills. Use a separate useEffect because offsets for order or position lines do not depend on the fills
  useEffect(() => {
    if (!isChartReady()) return;

    // Skip fills rendering when using Pyth feed
    if (!useArchiveDb) return;

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) return;
    // Skip rendering if resolution is transitioning
    // We need to check transitionRef.current directly here for the most up-to-date value
    if (resolutionTransition.transitionRef.current) {
      return;
    }

    tryCatch(() =>
      renderFills(
        chart,
        fills || [],
        fillsMap.current,
        productDecimals,
        priceDecimals,
        product.tickSize,
        showFillsSetting
      )
    );
  }, [
    isChartReady,
    doesSymbolMatch,
    showFillsSetting,
    fills,
    symbol,
    productDecimals,
    priceDecimals,
    product.tickSize,
    chartResolution,
    resolutionTransition.isTransitioning,
    resolutionTransition.transitionRef,
  ]);

  // Handle chart action queue, which allows the parent component to trigger calls
  // to executeActionById without needing access to the widget ref.
  useEffect(() => {
    if (!chartActionQueue || chartActionQueue.length === 0 || !isChartReady()) return;

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) return;

    // Process all actions in the queue
    chartActionQueue.forEach((action) => {
      tryCatch(() => chart.executeActionById(action));
    });

    // Clear the queue after processing
    setChartActionQueue?.([]);
  }, [chartActionQueue, isChartReady, doesSymbolMatch, setChartActionQueue]);

  return (
    <div className={twMerge('relative size-full', className)}>
      {isLoading || tradingViewPaused ? (
        <div
          className={cn(
            'absolute inset-0 z-10 flex items-center justify-center text-contentTertiary bg-backgroundPrimary',
            className
          )}
        >
          <div className="rounded-full bg-backgroundPrimary p-2">
            <AnimatedLogo size={60} autoPlay={true} />
          </div>
        </div>
      ) : null}
      <div id="tv_chart_container" className="size-full overflow-hidden rounded-lg" />
      {tradingViewPaused ? null : (
        <>
          <SymbolUpdater
            widgetRef={widgetRef}
            isChartReady={isChartReady}
            doesSymbolMatch={doesSymbolMatch}
            symbol={symbol}
            priceDecimals={priceDecimals}
            tickSize={tickSize}
            onSymbolChangeStart={symbolTransition.startTransition}
            onSymbolChangeComplete={symbolTransition.endTransition}
          />
          <StateToChartResolutionSync
            widgetRef={widgetRef}
            isChartReady={isChartReady}
            doesSymbolMatch={doesSymbolMatch}
            chartResolution={chartResolution}
            setChartResolution={setChartResolution}
            setChartType={setChartType}
          />
          <ChartToStateResolutionSync
            widgetRef={widgetRef}
            isChartReady={isChartReady}
            doesSymbolMatch={doesSymbolMatch}
            chartResolution={chartResolution}
            chartType={chartType}
            isLoading={isLoading}
            onResolutionChangeStart={resolutionTransition.startTransition}
            onResolutionChangeComplete={resolutionTransition.endTransition}
          />
          <LiqPriceLineUpdater
            product={product}
            widgetRef={widgetRef}
            isChartReady={isChartReady}
            doesSymbolMatch={doesSymbolMatch}
            isSwitchingSymbol={symbolTransition.isTransitioning}
            position={position}
            orders={orders}
            stopLossOrder={stopLossOrder}
            takeProfitOrder={takeProfitOrder}
            priceDecimals={priceDecimals}
            priceType={priceType}
          />
        </>
      )}
    </div>
  );
};

export const TradingViewChart: React.FC<TradingViewChartProps> = (props) => (
  <ErrorBoundary
    fallback={
      <div
        className={cn('flex size-full flex-col items-center justify-center gap-3', props.className)}
      >
        <p className="text-xs font-medium text-contentTertiary">Failed to load chart.</p>
        <p className="text-xs text-contentSecondary">Try reloading the page.</p>
      </div>
    }
  >
    <TVChartContainerInner {...props} />
  </ErrorBoundary>
);
